import { useEffect, useMemo } from "react"
import { useAppDispatch } from "../../reduxStore/hooks"
import { useSelector } from "react-redux"
import { getAllDetachedProviders } from "../../domain/Practices/loader"
import { selectPracticeInvites } from "../../domain/Practices/selectors"
import { selectAccountGuid } from "../authentication/authenticationSlice"
import { PracticeServiceInviteStatusDisplayId } from "../../domain/Practice/schema"

/**
 * Custom hook to automatically fetch any detached providers associated to the users practice invites.
 *
 * Note: there should technically only be ONE provider per user, so we should expect a singleton provider array.
 * However, we are using a singleton array to allow for full use of the endpoint
 * and possible extension of the provider to user model.
 *
 * @returns {DataStatus} The current status of the auto-linking process
 */
export const useFetchDetachedProviders = () => {
  const dispatch = useAppDispatch()
  const userId = useSelector(selectAccountGuid)
  const invites = useSelector(selectPracticeInvites)

  const providerIdsFromInvites: string[] = useMemo(() => {
    const unactionedInvites = invites.filter((invite) =>
      [
        PracticeServiceInviteStatusDisplayId.INVITED,
        PracticeServiceInviteStatusDisplayId.PENDING,
      ].includes(invite.inviteStatusDisplayId),
    )
    const unactionedInvitesProviderIds = unactionedInvites
      .map((invite) => invite.providerId)
      .filter((providerId): providerId is string => !!providerId)
    return unactionedInvitesProviderIds
  }, [invites])

  useEffect(() => {
    if (userId && providerIdsFromInvites.length > 0) {
      dispatch(getAllDetachedProviders({ providerIds: providerIdsFromInvites }))
    }
  }, [dispatch, providerIdsFromInvites, userId])
}
